/**
 * The definition of every object in the app.
 *
 * States, labels, tones, icons, allowed moves and verbs are written here and
 * nowhere else. The status chips, the pickers, the menus and Leo read them
 * through `lib/objects/registry`. Adding a state or changing who may do a verb
 * is one edit in this file.
 */

import { BLUEPRINT_ICON } from "@/lib/blueprint/icons"
import type { BlueprintStatus } from "@/lib/blueprint/types"
import {
  QUESTION_STATUS_DESCRIPTION,
  QUESTION_STATUS_ICON,
  QUESTION_STATUS_LABEL,
  QUESTION_STATUS_TONE,
  toQuestionStatus,
  QUESTION_STATUSES,
} from "@/lib/question-status"
import type {
  Assessment,
  CourseOffering,
  FacultyRecord,
  StudentRecord,
  Submission,
} from "@/lib/mock/exam-data"

import { defineObject } from "./registry"
import type { ActionDefinition, StatusState } from "./types"

type Rec = { id: string; [key: string]: any }

const ALL = ["Admin", "Faculty", "Student"] as const
const STAFF = ["Admin", "Faculty"] as const
const DIRECTOR = ["Admin"] as const

// ─── State tables (also read by the status chips) ───────────────────────────

export const ASSESSMENT_STATES: Record<Assessment["status"], StatusState> = {
  Draft: { label: "Draft", tone: "neutral", icon: "fa-pen-to-square", description: "Being built. Not visible to students." },
  Scheduled: { label: "Scheduled", tone: "info", icon: "fa-calendar-day", description: "Dates set. Waiting to open." },
  Live: { label: "Live", tone: "success", icon: "fa-signal-stream", description: "Open now. Students can sit it." },
  Evaluating: { label: "Evaluating", tone: "warning", icon: "fa-clipboard-list-check", description: "Closed. Scoring and grading in progress." },
  "Results Published": { label: "Results Published", tone: "success", icon: "fa-circle-check", description: "Results are out. The paper is final." },
}

/**
 * How an assessment counts in a person's summary: what is still being built,
 * what is out for students, what is being scored, what is finished. Every
 * "N completed / grading / published / draft" tally reads this, not its own
 * list of status words.
 */
export type AssessmentTallyGroup = "draft" | "published" | "grading" | "completed"

export function assessmentTallyGroup(status: string): AssessmentTallyGroup | null {
  switch (status) {
    case "Draft":
      return "draft"
    case "Scheduled":
    case "Live":
      return "published"
    case "Evaluating":
      return "grading"
    case "Results Published":
      return "completed"
    default:
      return null
  }
}

export function tallyAssessments(statuses: readonly string[]): Record<AssessmentTallyGroup, number> {
  const tally = { draft: 0, published: 0, grading: 0, completed: 0 }
  for (const status of statuses) {
    const group = assessmentTallyGroup(status)
    if (group) tally[group] += 1
  }
  return tally
}

export const SUBMISSION_STATES: Record<Exclude<Submission["status"], "">, StatusState> = {
  Graded: { label: "Graded", tone: "success", icon: "fa-circle-check", description: "Every item is scored." },
  "In-grading": { label: "In-grading", tone: "warning", icon: "fa-clock", description: "Scoring is still owed." },
}

const BLUEPRINT_STATES: Record<BlueprintStatus, StatusState> = {
  draft: { label: "Draft", tone: "neutral", icon: "fa-pen", description: "Still being allocated." },
  ready: { label: "Ready", tone: "success", icon: "fa-circle-check", description: "Complete. Papers can be checked against it." },
}

const COURSE_STATES: Record<CourseOffering["status"], StatusState> = {
  Active: { label: "Active", tone: "success", icon: "fa-circle-check" },
  Draft: { label: "Draft", tone: "neutral", icon: "fa-pen" },
  Archived: { label: "Archived", tone: "neutral", icon: "fa-box-archive" },
}

const PERSON_STATES: Record<"Active" | "Inactive", StatusState> = {
  Active: { label: "Active", tone: "success", icon: "fa-circle-check" },
  Inactive: { label: "Inactive", tone: "neutral", icon: "fa-circle-pause" },
}

const QUESTION_STATES: Record<string, StatusState> = Object.fromEntries(
  QUESTION_STATUSES.map((key) => [
    key,
    {
      label: QUESTION_STATUS_LABEL[key],
      tone: QUESTION_STATUS_TONE[key],
      icon: QUESTION_STATUS_ICON[key],
      description: QUESTION_STATUS_DESCRIPTION[key],
    },
  ]),
)

// ─── Objects ────────────────────────────────────────────────────────────────

const act = <T extends Rec>(a: ActionDefinition<T>) => a

export const QUESTION = defineObject<Rec>({
  type: "question",
  singular: "Question",
  plural: "Questions",
  icon: "fa-circle-question",
  tracksHistory: true,
  titleOf: (q) => String(q.title ?? q.stem ?? q.id),
  status: {
    of: (q) => toQuestionStatus(q.status),
    states: QUESTION_STATES,
    transitions: { Draft: ["Published"], Published: ["Draft"] },
  },
  actions: [
    act({ id: "edit", label: "Edit", icon: "fa-pen", roles: STAFF }),
    act({ id: "publish", label: "Publish", icon: "fa-circle-check", roles: STAFF, when: (q) => toQuestionStatus(q.status) === "Draft" }),
    act({ id: "unpublish", label: "Move to draft", icon: "fa-pen", roles: STAFF, when: (q) => toQuestionStatus(q.status) === "Published" }),
    act({ id: "add-to-assessment", label: "Add to assessment", icon: "fa-plus", roles: STAFF, when: (q) => toQuestionStatus(q.status) === "Published" }),
    act({ id: "categorise", label: "Categorise", icon: "fa-tag", roles: STAFF }),
    act({ id: "ask-leo", label: "Ask Leo", icon: "fa-sparkles", roles: STAFF }),
    act({ id: "history", label: "View history", icon: "fa-clock-rotate-left", roles: STAFF }),
    act({ id: "favorite", label: "Favorite", icon: "fa-star", roles: STAFF, bulk: true, shortcutKey: "F", tooltip: "Toggle favorite on selected questions" }),
    act({ id: "duplicate", label: "Duplicate", icon: "fa-clone", roles: STAFF, bulk: true, shortcutKey: "D", tooltip: "Duplicate selected questions" }),
    act({ id: "archive", label: "Archive", icon: "fa-box-archive", roles: STAFF, bulk: true, shortcutKey: "A", tooltip: "Archive selected questions" }),
    act({ id: "delete", label: "Delete", icon: "fa-trash-can", roles: STAFF, danger: true, bulk: true, shortcutKey: "⌫", tooltip: "Delete selected questions", confirm: "Delete this question? This cannot be undone." }),
    act({ id: "add-to-section", label: "Add to section", icon: "fa-plus", roles: STAFF, bulk: true, shortcutKey: "A", when: (q) => toQuestionStatus(q.status) === "Published" }),
    act({ id: "delete-imported", label: "Delete from Imported", icon: "fa-trash-can", roles: STAFF, danger: true, bulk: true, shortcutKey: "⌫", tooltip: "Delete the selected imported questions" }),
    // Import review: the same two verbs, worded for where the question lands.
    act({ id: "import", label: "Import", icon: "fa-plus", roles: STAFF, shortcutKey: "A" }),
    act({ id: "add-to-pool", label: "Add to Pool", icon: "fa-plus", roles: STAFF, shortcutKey: "A" }),
    act({ id: "remove-import", label: "Remove from bank", icon: "fa-minus", roles: STAFF, shortcutKey: "A" }),
    act({ id: "remove-from-pool", label: "Remove from Pool", icon: "fa-minus", roles: STAFF, shortcutKey: "A" }),
    act({ id: "categorise-bulk", label: "Categorise", icon: "fa-tag", roles: STAFF, bulk: true, shortcutKey: "C", tooltip: "Set categories and standards on the selected questions" }),
    act({ id: "organize", label: "Organize", icon: "fa-folder-tree", roles: STAFF, bulk: true, shortcutKey: "O", tooltip: "Move selected questions to a folder" }),
  ],
})

export const ASSESSMENT = defineObject<Rec>({
  type: "assessment",
  singular: "Assessment",
  plural: "Assessments",
  icon: "fa-file-lines",
  tracksHistory: true,
  titleOf: (a) => String(a.title ?? a.id),
  status: {
    of: (a) => String(a.status),
    states: ASSESSMENT_STATES,
    transitions: {
      Draft: ["Scheduled"],
      Scheduled: ["Draft", "Live"],
      Live: ["Evaluating"],
      Evaluating: ["Results Published"],
      "Results Published": [],
    },
  },
  actions: [
    act({ id: "edit", label: "Edit", icon: "fa-pen", roles: STAFF, when: (a) => a.status === "Draft" || a.status === "Scheduled" }),
    act({ id: "add-questions", label: "Add questions", icon: "fa-plus", roles: STAFF, when: (a) => a.status === "Draft" || a.status === "Scheduled" }),
    act({ id: "schedule", label: "Schedule", icon: "fa-calendar-clock", roles: STAFF, bulk: true, shortcutKey: "S", when: (a) => a.status === "Draft" }),
    act({ id: "duplicate", label: "Duplicate", icon: "fa-copy", roles: STAFF, bulk: true, shortcutKey: "D" }),
    act({ id: "export", label: "Export", icon: "fa-arrow-down-to-line", roles: STAFF, bulk: true, shortcutKey: "E" }),
    act({ id: "archive", label: "Archive", icon: "fa-box-archive", roles: STAFF, danger: true, bulk: true, shortcutKey: "A" }),
    act({ id: "go-live", label: "Go live", icon: "fa-signal-stream", roles: STAFF, shortcutKey: "L", when: (a) => a.status === "Scheduled" }),
    act({ id: "release-results", label: "Release results", icon: "fa-paper-plane", roles: DIRECTOR, shortcutKey: "R", when: (a) => a.status === "Evaluating" }),
    act({ id: "adjust-key", label: "Adjust key", icon: "fa-key", roles: STAFF, when: (a) => a.status === "Evaluating" }),
    act({ id: "delete", label: "Delete assessment", icon: "fa-trash-can", roles: STAFF, danger: true, confirm: "Delete this assessment?" }),
    act({ id: "history", label: "History", icon: "fa-clock-rotate-left", roles: STAFF }),
  ],
})

export const BLUEPRINT = defineObject<Rec>({
  type: "blueprint",
  singular: "Blueprint",
  plural: "Blueprints",
  icon: "fa-diagram-project",
  tracksHistory: true,
  titleOf: (b) => String(b.name ?? b.id),
  status: {
    of: (b) => String(b.status),
    states: BLUEPRINT_STATES,
    transitions: { draft: ["ready"], ready: ["draft"] },
  },
  actions: [
    act({ id: "edit", label: "Edit blueprint", icon: BLUEPRINT_ICON, roles: STAFF }),
    act({ id: "mark-ready", label: "Mark ready", icon: "fa-circle-check", roles: STAFF, when: (b) => b.status === "draft" }),
    act({ id: "check-coverage", label: "Check coverage", icon: "fa-chart-simple", roles: STAFF }),
    act({ id: "history", label: "History", icon: "fa-clock-rotate-left", roles: STAFF }),
  ],
})

export const SUBMISSION = defineObject<Rec>({
  type: "submission",
  singular: "Submission",
  plural: "Submissions",
  icon: "fa-file-pen",
  tracksHistory: true,
  titleOf: (s) => `${s.studentName ?? "Student"} · ${s.assessmentTitle ?? ""}`.trim(),
  status: {
    of: (s) => String(s.status || "In-grading"),
    states: SUBMISSION_STATES,
    transitions: { "In-grading": ["Graded"], Graded: ["In-grading"] },
  },
  actions: [
    act({ id: "grade", label: "Grade", icon: "fa-pen-line", roles: STAFF, when: (s) => s.status !== "Graded" }),
    act({ id: "review", label: "Review response", icon: "fa-eye", roles: ALL }),
    act({ id: "release", label: "Release grades", icon: "fa-paper-plane", roles: STAFF, bulk: true, shortcutKey: "R", tooltip: "Publish scores to the selected students" }),
    act({ id: "assign", label: "Assign grader", icon: "fa-user-check", roles: STAFF, bulk: true, shortcutKey: "G" }),
    act({ id: "export", label: "Export", icon: "fa-arrow-down-to-line", roles: STAFF, bulk: true, shortcutKey: "E" }),
  ],
})

export const COURSE = defineObject<Rec>({
  type: "course",
  singular: "Course",
  plural: "Courses",
  icon: "fa-book",
  tracksHistory: false,
  titleOf: (c) => `${c.code ?? ""} ${c.title ?? ""}`.trim(),
  status: { of: (c) => String(c.status), states: COURSE_STATES },
  actions: [
    act({ id: "view", label: "View", icon: "fa-arrow-right", roles: ALL }),
    act({ id: "manage", label: "Manage", icon: "fa-gear", roles: STAFF }),
  ],
})

export const STUDENT = defineObject<Rec>({
  type: "student",
  singular: "Student",
  plural: "Students",
  icon: "fa-user-graduate",
  tracksHistory: false,
  titleOf: (s) => String(s.name ?? s.id),
  status: { of: (s) => String(s.status), states: PERSON_STATES },
  actions: [act({ id: "view", label: "View profile", icon: "fa-arrow-right", roles: STAFF })],
})

/**
 * A classification category (Content, a standard). Created and changed in
 * Settings › Classification; elsewhere it is only tagged, and only the program
 * director may go from there to edit it.
 */
export const CATEGORY = defineObject<Rec>({
  type: "category",
  singular: "Category",
  plural: "Categories",
  icon: "fa-tag",
  tracksHistory: false,
  titleOf: (c) => String(c.name ?? c.id),
  actions: [
    act({ id: "edit", label: "Edit", icon: "fa-pen", roles: DIRECTOR, tooltip: "Edit in Settings › Classification" }),
  ],
})

export const FACULTY = defineObject<Rec>({
  type: "faculty",
  singular: "Faculty",
  plural: "Faculty",
  icon: "fa-chalkboard-user",
  tracksHistory: false,
  titleOf: (f) => String(f.name ?? f.id),
  status: { of: (f) => String(f.status), states: PERSON_STATES },
  actions: [act({ id: "view", label: "View profile", icon: "fa-arrow-right", roles: STAFF })],
})
